/**
 * SettingsCookies — Iter 191h · Cookies & Visitor Data
 *
 * Squarespace-parity cookie banner configurator. The vendor tunes:
 *   • Whether a consent banner shows at all (enabled / disabled)
 *   • Consent mode: opt-in (GDPR strict) or opt-out (CCPA)
 *   • Position (bottom bar / bottom-left / bottom-right / top bar)
 *   • Theme (dark / light)
 *   • Headline + body copy
 *   • Button labels (Accept / Decline / Preferences)
 *   • Whether to show Decline & Preferences buttons
 *   • A "Privacy Policy" link URL
 *   • Cookie category toggles (Essential=on always, Analytics, Marketing, Personalization)
 *
 * Persistence:
 *   GET  /api/builder/settings                    → returns { cookies: {...} }
 *   PUT  /api/builder/settings  { cookies: {...} }
 *
 * The live banner renders on the public storefront (PublicSitePage.js) via
 * `<PublicCookieBanner cookies={site.settings.cookies} />`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Cookie, Loader2, Save } from 'lucide-react';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULTS = {
  enabled: true,
  mode: 'opt-in',                 // 'opt-in' | 'opt-out'
  position: 'bottom-left',        // 'bottom-left' | 'bottom-right' | 'bottom-bar' | 'top-bar'
  theme: 'dark',                  // 'dark' | 'light'
  headline: 'A note on cookies',
  body: 'We use cookies to improve your experience, measure performance and personalize content. You can accept all, decline non-essential, or manage preferences.',
  accept_label: 'Accept all',
  decline_label: 'Decline',
  preferences_label: 'Preferences',
  show_decline: true,
  show_preferences: false,
  privacy_link: '/privacy',
  categories: {
    essential: true,
    analytics: true,
    marketing: true,
    personalization: false,
  },
};

const MODES = [
  {
    id: 'opt-in',
    label: 'Opt-in (GDPR strict)',
    sub: 'No non-essential cookies fire until visitors accept. Recommended for EU, UK, and Brazil (LGPD) traffic.',
  },
  {
    id: 'opt-out',
    label: 'Opt-out (CCPA style)',
    sub: 'Cookies fire by default. Visitors can decline or disable specific categories. Suitable for California, Canada, most US traffic.',
  },
];

const POSITIONS = [
  { id: 'bottom-left',   label: 'Bottom left (card)' },
  { id: 'bottom-right',  label: 'Bottom right (card)' },
  { id: 'bottom-bar',    label: 'Bottom bar (full width)' },
  { id: 'top-bar',       label: 'Top bar (full width)' },
];

function Toggle({ checked, onChange, disabled = false, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      data-testid={testid}
      className={`relative w-10 h-6 rounded-full transition-colors ${
        checked ? 'bg-[#A78BFA]' : 'bg-white/15'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`absolute top-[2px] left-[2px] w-5 h-5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <div className="py-4 border-b border-white/[0.06] last:border-b-0" data-testid={testid}>
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-white">{label}</div>
          {hint && <div className="mt-1 text-[12px] text-[#94A3B8] leading-snug">{hint}</div>}
        </div>
        <div className="shrink-0">{children}</div>
      </div>
    </div>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <section className="mt-8 rounded-xl bg-white/[0.03] border border-white/[0.08] p-6">
      <h3 className="text-[15px] font-bold text-white tracking-tight">{title}</h3>
      {description && <p className="mt-1 text-[12.5px] text-[#94A3B8] leading-snug">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ModeRadio({ option, active, onSelect }) {
  return (
    <div
      onClick={() => onSelect(option.id)}
      className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all border ${
        active
          ? 'border-[#A78BFA]/60 bg-[#A78BFA]/10'
          : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05]'
      }`}
      data-testid={`cookies-mode-${option.id}`}
    >
      <div className="pt-0.5">
        <div className={`w-4 h-4 rounded-full border-2 grid place-items-center ${active ? 'border-[#A78BFA]' : 'border-white/25'}`}>
          {active && <div className="w-2 h-2 rounded-full bg-[#A78BFA]" />}
        </div>
      </div>
      <div className="flex-1">
        <div className="text-[13px] font-semibold text-white">{option.label}</div>
        <p className="mt-1 text-[12px] text-[#94A3B8] leading-snug">{option.sub}</p>
      </div>
    </div>
  );
}

/* --------------------------- Live preview banner --------------------------- */
function PreviewBanner({ cookies }) {
  const isDark = cookies.theme === 'dark';
  const isBar = cookies.position === 'bottom-bar' || cookies.position === 'top-bar';

  const bg = isDark
    ? 'bg-[#0B0B24]/95 border-white/10 text-white'
    : 'bg-white/95 border-black/10 text-[#0E0E23]';
  const secondaryBtn = isDark
    ? 'bg-white/5 border border-white/15 text-white hover:bg-white/10'
    : 'bg-black/5 border border-black/10 text-[#0E0E23] hover:bg-black/10';

  return (
    <div className={`relative w-full h-[240px] rounded-lg overflow-hidden ${isDark ? 'bg-[#12122A]' : 'bg-[#F4F4FF]'}`}>
      {/* Fake site chrome */}
      <div className={`absolute inset-0 ${isDark ? 'opacity-40' : 'opacity-70'}`}>
        <div className="h-6 border-b border-white/5 flex items-center gap-1.5 px-3">
          <span className="w-2 h-2 rounded-full bg-red-400/60" />
          <span className="w-2 h-2 rounded-full bg-yellow-400/60" />
          <span className="w-2 h-2 rounded-full bg-green-400/60" />
        </div>
        <div className="p-5 space-y-2">
          <div className={`h-3 w-40 rounded ${isDark ? 'bg-white/10' : 'bg-black/10'}`} />
          <div className={`h-2 w-60 rounded ${isDark ? 'bg-white/8' : 'bg-black/8'}`} />
          <div className={`h-2 w-52 rounded ${isDark ? 'bg-white/8' : 'bg-black/8'}`} />
        </div>
      </div>

      {/* Banner */}
      {cookies.enabled && (
        <div
          className={`absolute ${
            cookies.position === 'top-bar' ? 'top-6 left-0 right-0' :
            cookies.position === 'bottom-bar' ? 'bottom-0 left-0 right-0' :
            cookies.position === 'bottom-right' ? 'bottom-3 right-3 max-w-[280px]' :
            'bottom-3 left-3 max-w-[280px]'
          }`}
        >
          <div className={`p-3 ${isBar ? 'px-5 flex items-center gap-3' : 'rounded-lg'} ${bg} border shadow-lg`}>
            <div className={`flex-1 min-w-0 ${isBar ? '' : 'mb-2'}`}>
              <div className="text-[11px] font-bold flex items-center gap-1.5">
                <Cookie size={11} /> {cookies.headline || 'A note on cookies'}
              </div>
              <div className={`text-[10px] leading-snug mt-0.5 line-clamp-2 ${isDark ? 'text-white/70' : 'text-[#0E0E23]/70'}`}>
                {cookies.body}
              </div>
            </div>
            <div className={`flex items-center gap-1.5 ${isBar ? '' : ''}`}>
              {cookies.show_preferences && (
                <span className={`px-2 py-1 text-[9px] font-semibold rounded ${secondaryBtn}`}>
                  {cookies.preferences_label}
                </span>
              )}
              {cookies.show_decline && (
                <span className={`px-2 py-1 text-[9px] font-semibold rounded ${secondaryBtn}`}>
                  {cookies.decline_label}
                </span>
              )}
              <span className="px-2 py-1 text-[9px] font-bold rounded bg-[#A78BFA] text-[#0E0E23]">
                {cookies.accept_label}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- Page -------------------------------- */
export default function SettingsCookies() {
  const [cookies, setCookies] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/builder/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const remote = res.data?.cookies;
        if (remote && typeof remote === 'object') {
          setCookies({
            ...DEFAULTS,
            ...remote,
            categories: { ...DEFAULTS.categories, ...(remote.categories || {}) },
          });
        }
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [token]);

  const update = (patch) => {
    setCookies((c) => ({ ...c, ...patch }));
    setDirty(true);
  };
  const updateCategory = (key, val) => {
    setCookies((c) => ({ ...c, categories: { ...c.categories, [key]: val } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, { cookies }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Cookie settings saved');
      setDirty(false);
    } catch {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const memoPreview = useMemo(() => cookies, [cookies]);

  return (
    <div className="min-h-full" data-testid="settings-cookies-page">
      {/* Top rail */}
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackToSettings />
          <div className="w-px h-4 bg-white/10" />
          <h1 className="text-[13px] font-bold text-white uppercase tracking-[0.18em]">Settings</h1>
        </div>
        <div className="flex items-center gap-3">
          <button className="w-8 h-8 grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-md transition-all" title="Search settings">
            <Search size={14} />
          </button>
          {saving && (
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#A78BFA] font-bold animate-pulse">Saving…</span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] transition-all ${
              dirty && !saving
                ? 'bg-[#A78BFA] text-[#0E0E23] hover:bg-white'
                : 'bg-white/5 text-white/40 cursor-not-allowed'
            }`}
            data-testid="cookies-save-btn"
          >
            <Save size={12} /> Save
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-10">
        {/* Breadcrumb */}
        <div className="text-[12px] text-[#64748B] mb-3">
          <span className="text-white/70 font-semibold">Website</span>
          <span className="mx-2">/</span>
          <span className="text-white">Cookies &amp; Visitor Data</span>
        </div>

        <h2 className="text-[32px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>
          Cookies &amp; Visitor Data
        </h2>
        <p className="mt-3 text-[14px] text-[#9CA3AF] leading-relaxed max-w-2xl">
          Configure how your storefront asks visitors for consent, and manage the cookie categories that track
          activity on your site. Comply with GDPR, CCPA, LGPD, and other regional privacy laws.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-white/50">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Live preview */}
            <SectionCard
              title="Live preview"
              description="This is roughly how the banner will appear on your live storefront."
            >
              <PreviewBanner cookies={memoPreview} />
            </SectionCard>

            {/* Banner display */}
            <SectionCard
              title="Banner"
              description="Turn the consent banner on or off, and choose where it appears."
            >
              <Field
                label="Show cookie banner"
                hint="Visitors see a consent banner on their first visit."
                testid="field-banner-enabled"
              >
                <Toggle checked={cookies.enabled} onChange={(v) => update({ enabled: v })} testid="cookies-enabled-toggle" />
              </Field>

              <Field label="Position" hint="Where the banner sits on the page." testid="field-position">
                <select
                  value={cookies.position}
                  onChange={(e) => update({ position: e.target.value })}
                  disabled={!cookies.enabled}
                  className="bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60 min-w-[200px]"
                  data-testid="cookies-position-select"
                >
                  {POSITIONS.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Theme" hint="Match your storefront's look." testid="field-theme">
                <div className="inline-flex rounded-md border border-white/10 overflow-hidden">
                  {['dark', 'light'].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => update({ theme: t })}
                      disabled={!cookies.enabled}
                      className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-wider ${
                        cookies.theme === t
                          ? 'bg-[#A78BFA] text-[#0E0E23]'
                          : 'bg-transparent text-white/60 hover:bg-white/5'
                      }`}
                      data-testid={`cookies-theme-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </Field>
            </SectionCard>

            {/* Consent mode */}
            <SectionCard
              title="Consent mode"
              description="Determine whether cookies fire before or after visitor consent."
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {MODES.map((m) => (
                  <ModeRadio key={m.id} option={m} active={cookies.mode === m.id} onSelect={(v) => update({ mode: v })} />
                ))}
              </div>
            </SectionCard>

            {/* Copy */}
            <SectionCard
              title="Banner copy"
              description="Customize the words visitors see. Keep it brief and human."
            >
              <div className="space-y-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Headline</span>
                  <input
                    type="text"
                    value={cookies.headline}
                    onChange={(e) => update({ headline: e.target.value })}
                    maxLength={80}
                    className="mt-2 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-white/25 focus:outline-none focus:border-[#A78BFA]/60"
                    data-testid="cookies-headline-input"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Body</span>
                  <textarea
                    value={cookies.body}
                    onChange={(e) => update({ body: e.target.value })}
                    maxLength={400}
                    rows={3}
                    className="mt-2 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-white/25 focus:outline-none focus:border-[#A78BFA]/60 resize-y"
                    data-testid="cookies-body-input"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Privacy policy link</span>
                  <input
                    type="text"
                    value={cookies.privacy_link}
                    onChange={(e) => update({ privacy_link: e.target.value })}
                    placeholder="/privacy or https://…"
                    className="mt-2 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-white/25 focus:outline-none focus:border-[#A78BFA]/60"
                    data-testid="cookies-privacy-link-input"
                  />
                </label>
              </div>
            </SectionCard>

            {/* Buttons */}
            <SectionCard
              title="Buttons"
              description="Label and toggle the actions visitors can take from the banner."
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Accept label</span>
                  <input
                    type="text"
                    value={cookies.accept_label}
                    onChange={(e) => update({ accept_label: e.target.value })}
                    maxLength={30}
                    className="mt-2 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                    data-testid="cookies-accept-label-input"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Decline label</span>
                  <input
                    type="text"
                    value={cookies.decline_label}
                    onChange={(e) => update({ decline_label: e.target.value })}
                    maxLength={30}
                    className="mt-2 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                    data-testid="cookies-decline-label-input"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Preferences label</span>
                  <input
                    type="text"
                    value={cookies.preferences_label}
                    onChange={(e) => update({ preferences_label: e.target.value })}
                    maxLength={30}
                    className="mt-2 w-full bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                    data-testid="cookies-preferences-label-input"
                  />
                </label>
              </div>

              <div className="mt-4">
                <Field
                  label="Show decline button"
                  hint="Let visitors decline all non-essential cookies with one click. Required in some regions."
                  testid="field-show-decline"
                >
                  <Toggle checked={cookies.show_decline} onChange={(v) => update({ show_decline: v })} testid="cookies-show-decline-toggle" />
                </Field>
                <Field
                  label="Show preferences button"
                  hint="Let visitors pick which cookie categories they allow."
                  testid="field-show-preferences"
                >
                  <Toggle checked={cookies.show_preferences} onChange={(v) => update({ show_preferences: v })} testid="cookies-show-preferences-toggle" />
                </Field>
              </div>
            </SectionCard>

            {/* Categories */}
            <SectionCard
              title="Cookie categories"
              description="Group the cookies your site uses. Visitors can allow or block each category from the preferences panel."
            >
              <Field
                label="Essential"
                hint="Required for the site to function (auth, cart, checkout). Cannot be disabled."
                testid="field-cat-essential"
              >
                <Toggle checked={true} onChange={() => {}} disabled testid="cookies-cat-essential" />
              </Field>
              <Field
                label="Analytics"
                hint="Google Analytics, Squarespace analytics, page-view tracking. Helps you understand traffic."
                testid="field-cat-analytics"
              >
                <Toggle checked={cookies.categories.analytics} onChange={(v) => updateCategory('analytics', v)} testid="cookies-cat-analytics" />
              </Field>
              <Field
                label="Marketing"
                hint="Meta Pixel, Google Ads, TikTok Pixel. Used to measure ad performance and retarget visitors."
                testid="field-cat-marketing"
              >
                <Toggle checked={cookies.categories.marketing} onChange={(v) => updateCategory('marketing', v)} testid="cookies-cat-marketing" />
              </Field>
              <Field
                label="Personalization"
                hint="Save visitor preferences (theme, language) between sessions."
                testid="field-cat-personalization"
              >
                <Toggle checked={cookies.categories.personalization} onChange={(v) => updateCategory('personalization', v)} testid="cookies-cat-personalization" />
              </Field>
            </SectionCard>

            {/* Sticky save bar spacer */}
            <div className="h-16" />
          </>
        )}
      </div>
    </div>
  );
}
